<template>
  <section class="tabular-import" aria-label="Import tabular dataset" :aria-busy="busy">
    <h3><i class="bi bi-table" aria-hidden="true"></i> Import dataset<HelpLink topic="datasets" section="importing-a-csv-file" title="Step-by-step import guide" /></h3>
    <p class="hint">Upload one delimited file, preview its profile, then queue an import. A valid sample does not guarantee every row will import.</p>
    <p v-if="!identityValid" class="alert-error" role="alert">A signed-in account and experiment are required.</p>
    <p v-if="error" class="alert-error" role="alert">{{ error }}</p>
    <p v-if="queuedDataset" class="notice" role="status">Dataset {{ queuedDataset.id }} queued. Import is not complete; the dataset panel tracks processing.</p>

    <form @submit.prevent="requestPreview">
      <fieldset :disabled="locked || uploading || !identityValid">
        <legend>Source and schema</legend>
        <label class="form-group">
          <span>Upload source file</span>
          <input ref="sourceInput" type="file" accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values" @change="selectFile" />
        </label>
        <p v-if="sourceFileId" class="notice">Committed source file ID: {{ sourceFileId }}. Cancelling an import does not delete this file.</p>
        <label class="form-group"><span>Dataset title</span><input v-model="title" type="text" maxlength="255" required /></label>
        <label class="form-group">
          <span>Schema</span>
          <select v-model="schemaId" :disabled="loadingSchemas" @change="loadSchema">
            <option value="">Create schema from file (read-only suggested columns)</option>
            <option v-for="schema in schemas" :key="schema.id" :value="schema.id">{{ schema.name }}</option>
          </select>
        </label>
        <p v-if="loadingSchemas || loadingSchema" role="status">Loading schemas…</p>
        <div v-if="schemaError" class="alert-error" role="alert">
          {{ schemaError }}
          <button type="button" class="btn-secondary" @click="schemaId ? loadSchema() : loadSchemas()">Retry schema loading</button>
        </div>
        <label v-if="schemaId" class="form-group">
          <span>Immutable schema version</span>
          <select v-model="versionId" :disabled="loadingSchema" @change="applyVersionDefaults">
            <option value="">Choose a version</option>
            <option v-for="version in versions" :key="version.id" :value="version.id">Version {{ version.version_number }} (ID {{ version.id }})</option>
          </select>
        </label>
        <template v-else>
          <label class="form-group"><span>New schema name</span><input v-model="schemaName" type="text" maxlength="255" required /></label>
          <label class="form-group"><span>New schema description</span><textarea v-model="schemaDescription" maxlength="10000" rows="2"></textarea></label>
          <label class="checkbox-label"><input v-model="schemaReusable" type="checkbox" /> Make schema reusable</label>
        </template>
      </fieldset>

      <fieldset :disabled="locked || uploading || !identityValid">
        <legend>Parser options</legend>
        <p class="hint">Changing these fields invalidates the preview immediately. Leave mapping blank to request server detection; a custom mapping must cover every schema key.</p>
        <div class="option-grid">
          <label class="form-group"><span>Delimiter (one character; paste a tab for TSV)</span><input v-model="delimiter" type="text" required /></label>
          <label class="form-group"><span>Encoding</span><select v-model="encoding"><option value="utf-8-sig">utf-8-sig</option><option value="utf-8">utf-8</option></select></label>
        </div>
        <label class="form-group"><span>Null values (JSON array of strings)</span><textarea v-model="nullValuesJson" rows="2" spellcheck="false"></textarea></label>
        <label class="form-group"><span>Complete mapping (JSON object: schema key → source header; blank = detect)</span><textarea v-model="mappingJson" rows="3" spellcheck="false" placeholder='{"column_key": "Source header"}'></textarea></label>
        <button type="submit" class="btn-primary" :disabled="!canPreview">{{ previewing ? 'Profiling…' : 'Preview file' }}</button>
      </fieldset>
    </form>

    <section v-if="preview" class="review" aria-label="Preview review">
      <h4>Review file profile</h4>
      <p :class="reviewError ? 'alert-error' : 'notice'" :role="reviewError ? 'alert' : 'status'">{{ reviewError || 'Profile valid. Review the exact effective options and suggested columns before confirming.' }}</p>
      <h5>Detected headers, sample rows and profile diagnostics</h5>
      <p v-if="preview.detected">Sample: {{ preview.detected.sample_rows }} rows, {{ preview.detected.sample_bytes }} bytes.</p>
      <div v-if="preview.detected && preview.detected.headers" class="table-scroll">
        <table aria-label="Sample rows">
          <thead><tr><th v-for="(header, index) in preview.detected.headers" :key="index">{{ header }}</th></tr></thead>
          <tbody><tr v-for="(row, index) in preview.detected.preview_rows" :key="index"><td v-for="(header, colIndex) in preview.detected.headers" :key="colIndex">{{ sampleCell(row, header, colIndex) }}</td></tr></tbody>
        </table>
      </div>
      <p v-if="preview.detected && preview.detected.structural_errors && preview.detected.structural_errors.length" class="alert-error">Structural errors: {{ pretty(preview.detected.structural_errors) }}</p>
      <p class="hint">Suggested columns cannot be edited. Confidence and diagnostics are advisory, not schema fields.</p>
      <div v-if="reviewColumns.length" class="table-scroll">
        <table aria-label="Suggested columns">
          <thead><tr><th>Key</th><th>Name</th><th>Logical type</th><th>Nullable</th><th>Unit</th><th>Confidence</th><th>Reasons</th><th>Null count</th></tr></thead>
          <tbody><tr v-for="column in reviewColumns" :key="keyForColumn(column)"><td>{{ keyForColumn(column) }}</td><td>{{ column.display_name }}</td><td>{{ column.logical_type }}</td><td>{{ column.nullable === undefined ? true : column.nullable }}</td><td>{{ column.unit }}</td><td>{{ column.confidence ?? 'Not supplied' }}</td><td>{{ column.reason ?? 'Not supplied' }}</td><td>{{ column.sampled_null ?? 'Not supplied' }}</td></tr></tbody>
        </table>
      </div>
      <details open><summary>Complete profile and all diagnostics</summary><pre>{{ pretty(preview) }}</pre></details>
      <details open><summary>Exact effective import options</summary><pre>{{ pretty(preview.import_options) }}</pre></details>
      <p class="hint">Confirmation uses the server's effective import_options without merging or changing them.</p>
      <button type="button" class="btn-primary" :disabled="!canConfirm" @click="submitImport">{{ submitting ? 'Submitting…' : 'Confirm and queue import' }}</button>
    </section>

    <section v-if="frozenPayload && !queuedDataset" class="attempt" aria-label="Confirmation attempt">
      <p class="notice">Confirmation is frozen. Fields remain locked so retries use the identical body and idempotency key.</p>
      <p v-if="retryAllowed" class="alert-error" role="alert">The outcome is uncertain. The server may already have queued this dataset. Retry the exact request, or explicitly abandon it below.</p>
      <button v-if="retryAllowed" type="button" class="btn-primary" :disabled="submitting" @click="submitImport">Retry identical confirmation</button>
      <p v-if="needsRepreview" class="alert-error" role="alert">The server rejected confirmation. Re-preview requires explicitly abandoning this attempt; no key or token has been silently replaced.</p>
      <details><summary>Frozen confirmation body</summary><pre>{{ pretty(frozenPayload) }}</pre></details>
      <label class="checkbox-label"><input v-model="abandonAcknowledged" type="checkbox" :disabled="submitting" /> I understand the previous request may still create a dataset; a new attempt can create a duplicate.</label>
      <button type="button" class="btn-secondary" :disabled="submitting || !abandonAcknowledged" @click="abandonAttempt">Abandon attempt and enable re-preview</button>
    </section>
    <button v-if="queuedDataset" type="button" class="btn-secondary" @click="startAnother">Import another dataset</button>

    <FileUploadSession
      v-if="uploadFiles.length"
      :key="uploadKey"
      :visible="uploading"
      file-type="experiment"
      :entity-id="experimentId"
      :entra-id="entraId"
      :files="uploadFiles"
      v-on="uploadEvents"
    />
  </section>
</template>

<script>
import apiService from "@/services/apiService";
import FileUploadSession from "@/components/FileUploadSession.vue";
import {
  positiveInteger, columnKey, copyJson, isObject, parseJson,
  snapshotColumns, validateImportOptions, reviewPreview, freezeConfirmation,
  ambiguousImportError, importErrorMessage,
} from "@/utils/tabularImport";

export default {
  name: "TabularDatasetImport",
  components: { FileUploadSession },
  props: {
    experimentId: { type: [Number, String], required: true },
    entraId: { type: String, required: true },
  },
  emits: ["queued", "fileCommitted", "busyChange", "unresolvedAttemptChange"],
  data() {
    return {
      sourceFileId: null, title: "", schemaId: "", versionId: "",
      schemaName: "", schemaDescription: "", schemaReusable: false,
      schemas: [], versions: [], loadingSchemas: false, loadingSchema: false, schemaError: "",
      delimiter: ",", encoding: "utf-8-sig", nullValuesJson: '[""]', mappingJson: "", defaultOptions: {},
      preview: null, previewing: false, error: "", submitting: false,
      frozenPayload: null, retryAllowed: false, needsRepreview: false, abandonAcknowledged: false,
      queuedDataset: null, uploading: false, uploadStarting: false, uploadFiles: [], uploadEvents: {}, uploadKey: 0,
      generation: 0, schemaGeneration: 0, epoch: 0, controllers: {}, disposed: false,
    };
  },
  computed: {
    busy() { return this.uploadStarting || this.uploading || this.submitting || this.previewing; },
    unresolvedAttempt() { return Boolean(this.frozenPayload && !this.queuedDataset); },
    identityValid() { return Boolean(positiveInteger(this.experimentId) && this.entraId?.trim()); },
    locked() { return Boolean(this.frozenPayload || this.queuedDataset); },
    selectedVersion() { return this.versions.find((version) => positiveInteger(version.id) === positiveInteger(this.versionId)) || null; },
    inputIdentity() {
      return JSON.stringify([this.sourceFileId, this.schemaId, this.versionId, this.delimiter,
        this.encoding, this.nullValuesJson, this.mappingJson, this.title,
        this.schemaName, this.schemaDescription, this.schemaReusable]);
    },
    canPreview() {
      return this.identityValid && Boolean(positiveInteger(this.sourceFileId)) && !this.locked && !this.uploading &&
        !this.previewing && !this.loadingSchema && (!this.schemaId || Boolean(this.selectedVersion));
    },
    reviewError() {
      if (!this.preview) return "Preview the file before confirming.";
      try { reviewPreview(this.preview, this.schemaId ? this.selectedVersion?.columns : null); return ""; }
      catch (error) { return error.message; }
    },
    reviewColumns() {
      return this.preview?.suggested_schema?.columns || this.selectedVersion?.columns || [];
    },
    canConfirm() {
      return this.identityValid && Boolean(this.preview) && !this.reviewError && !this.locked &&
        !this.submitting && !this.previewing && !this.uploading && Boolean(this.title.trim()) &&
        (this.schemaId ? Boolean(this.selectedVersion) : Boolean(this.schemaName.trim()));
    },
  },
  watch: {
    busy: { flush: "sync", immediate: true, handler(value) { this.$emit("busyChange", value); } },
    unresolvedAttempt: { flush: "sync", immediate: true, handler(value) { this.$emit("unresolvedAttemptChange", value); } },
    inputIdentity: { flush: "sync", handler() { this.invalidatePreview(); } },
    experimentId: { flush: "sync", handler() { this.resetContext(); } },
    entraId: { flush: "sync", handler() { this.resetContext(); } },
  },
  mounted() { this.loadSchemas(); },
  beforeUnmount() {
    this.disposed = true;
    this.epoch += 1;
    Object.values(this.controllers).forEach((controller) => controller.abort());
  },
  methods: {
    sampleCell(row, header, index) {
      const value = Array.isArray(row) ? row[index] : row?.[header];
      return value == null ? "—" : typeof value === "object" ? this.pretty(value) : String(value);
    },
    pretty(value) { return value === undefined ? "Not supplied" : JSON.stringify(value, null, 2); },
    keyForColumn: columnKey,
    controller(name) {
      this.controllers[name]?.abort();
      const controller = new AbortController();
      this.controllers[name] = controller;
      return controller;
    },
    current(epoch, controller) { return !this.disposed && epoch === this.epoch && !controller.signal.aborted; },
    invalidatePreview() {
      this.generation += 1;
      this.controllers.preview?.abort();
      this.preview = null;
      this.previewing = false;
    },
    resetContext() {
      this.epoch += 1;
      this.schemaGeneration += 1;
      Object.values(this.controllers).forEach((controller) => controller.abort());
      Object.assign(this, {
        sourceFileId: null, title: "", schemaId: "", versionId: "", schemaName: "",
        schemaDescription: "", schemaReusable: false, schemas: [], versions: [],
        loadingSchemas: false, loadingSchema: false, schemaError: "", error: "",
        delimiter: ",", encoding: "utf-8-sig", nullValuesJson: '[""]', mappingJson: "", defaultOptions: {},
        frozenPayload: null, submitting: false, retryAllowed: false, needsRepreview: false,
        abandonAcknowledged: false, queuedDataset: null, uploading: false, uploadStarting: false, uploadFiles: [], uploadEvents: {},
      });
      this.invalidatePreview();
      this.loadSchemas();
    },
    async loadSchemas() {
      if (!this.identityValid || this.disposed) return;
      const controller = this.controller("schemas");
      const epoch = this.epoch;
      this.loadingSchemas = true;
      this.schemaError = "";
      try {
        const response = await apiService.listTabularSchemas(this.entraId, controller.signal);
        if (!this.current(epoch, controller)) return;
        if (!Array.isArray(response?.schemas)) throw new Error("Server returned no schemas array.");
        this.schemas = response.schemas;
      } catch (error) {
        if (this.current(epoch, controller)) this.schemaError = importErrorMessage(error);
      } finally {
        if (this.current(epoch, controller)) this.loadingSchemas = false;
      }
    },
    async loadSchema() {
      this.invalidatePreview();
      const generation = ++this.schemaGeneration;
      this.controllers.schema?.abort();
      this.versions = [];
      this.versionId = "";
      this.defaultOptions = {};
      this.loadingSchema = false;
      this.schemaError = "";
      if (!this.schemaId || !this.identityValid) return;
      const controller = this.controller("schema");
      const epoch = this.epoch;
      this.loadingSchema = true;
      try {
        const response = await apiService.getTabularSchema(this.schemaId, this.entraId, controller.signal);
        if (!this.current(epoch, controller) || generation !== this.schemaGeneration) return;
        if (!Array.isArray(response?.schema?.versions)) throw new Error("Server returned no schema versions.");
        this.versions = [...response.schema.versions].sort((a, b) => b.version_number - a.version_number);
      } catch (error) {
        if (this.current(epoch, controller) && generation === this.schemaGeneration) this.schemaError = importErrorMessage(error);
      } finally {
        if (this.current(epoch, controller) && generation === this.schemaGeneration) this.loadingSchema = false;
      }
    },
    applyVersionDefaults() {
      this.invalidatePreview();
      const defaults = this.selectedVersion?.default_import_options;
      this.defaultOptions = isObject(defaults) ? copyJson(defaults) : {};
      this.delimiter = this.defaultOptions.delimiter ?? ",";
      this.encoding = this.defaultOptions.encoding ?? "utf-8-sig";
      this.nullValuesJson = JSON.stringify(this.defaultOptions.null_values ?? [""]);
      this.mappingJson = this.defaultOptions.column_mapping ? JSON.stringify(this.defaultOptions.column_mapping, null, 2) : "";
    },
    async selectFile(event) {
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file || this.locked || this.uploading || this.uploadStarting || !this.identityValid) return;
      this.uploadStarting = true;
      this.sourceFileId = null;
      this.invalidatePreview();
      this.error = "";
      if (!this.title) this.title = file.name;
      if (!this.schemaName) this.schemaName = file.name.replace(/\.[^.]+$/, "");
      const epoch = this.epoch;
      const uploadKey = ++this.uploadKey;
      const active = () => !this.disposed && epoch === this.epoch && uploadKey === this.uploadKey;
      this.uploadEvents = {
        fileCommitted: (record) => {
          if (!active()) return;
          const id = positiveInteger(record?.id);
          if (!id) { this.error = "Upload commit did not expose a positive integer record.id; preview is disabled."; return; }
          this.sourceFileId = id;
          this.$emit("fileCommitted", record);
        },
        complete: () => { if (active()) this.finishUpload(); },
        close: () => { if (active()) this.finishUpload(); },
        error: (error) => { if (active()) this.error = importErrorMessage(error); },
      };
      this.uploadFiles = [file];
      // FileUploadSession starts on a false → true visibility transition.
      await this.$nextTick();
      if (active()) {
        this.uploading = true;
        this.uploadStarting = false;
      }
    },
    finishUpload() {
      this.uploading = false;
      if (!this.sourceFileId && !this.error) this.error = "No committed source file ID was received. Upload a file before previewing.";
    },
    parserOptions() {
      const options = {
        ...copyJson(this.defaultOptions), delimiter: this.delimiter, encoding: this.encoding,
        null_values: parseJson(this.nullValuesJson, "Null values", "array"),
      };
      delete options.column_mapping;
      if (this.mappingJson.trim()) options.column_mapping = parseJson(this.mappingJson, "Column mapping", "object");
      validateImportOptions(options, this.schemaId && options.column_mapping ? snapshotColumns(this.selectedVersion.columns) : null);
      return options;
    },
    async requestPreview() {
      if (!this.canPreview) return;
      this.invalidatePreview();
      this.error = "";
      let payload;
      try {
        payload = { source_file_id: positiveInteger(this.sourceFileId), import_options: this.parserOptions() };
        if (this.schemaId) {
          const version = positiveInteger(this.selectedVersion?.id);
          if (!version) throw new Error("Choose a valid immutable schema version.");
          payload.schema_version_id = version;
        }
      } catch (error) { this.error = error.message; return; }
      const controller = this.controller("preview");
      const generation = this.generation;
      const epoch = this.epoch;
      this.previewing = true;
      try {
        const response = await apiService.previewDatasetImport(this.experimentId, payload, this.entraId, controller.signal);
        if (!this.current(epoch, controller) || generation !== this.generation) return;
        if (!isObject(response) || typeof response.profile_valid !== "boolean") throw new Error("Server returned no PreviewDatasetResponse.");
        this.preview = copyJson(response);
      } catch (error) {
        if (this.current(epoch, controller) && generation === this.generation) this.error = importErrorMessage(error);
      } finally {
        if (this.current(epoch, controller) && generation === this.generation) this.previewing = false;
      }
    },
    async submitImport() {
      if (this.submitting || this.queuedDataset || !this.identityValid) return;
      if (this.frozenPayload && !this.retryAllowed) return;
      this.error = "";
      try {
        if (!this.frozenPayload) {
          if (!this.canConfirm) return;
          this.frozenPayload = freezeConfirmation({
            sourceFileId: this.sourceFileId, entraId: this.entraId, title: this.title,
            preview: this.preview, schemaVersionId: this.schemaId ? this.versionId : null,
            columns: this.selectedVersion?.columns,
            newSchema: this.schemaId ? null : { name: this.schemaName, description: this.schemaDescription, is_reusable: this.schemaReusable },
          });
        }
      } catch (error) { this.error = error.message; return; }
      this.submitting = true;
      this.retryAllowed = false;
      this.needsRepreview = false;
      const controller = this.controller("submit");
      const epoch = this.epoch;
      try {
        const response = await apiService.createDatasetImport(this.experimentId, this.frozenPayload, this.entraId, controller.signal);
        if (!this.current(epoch, controller)) return;
        if (!isObject(response?.dataset) || typeof response.dataset.id !== "string" || !response.dataset.id.trim()) {
          // A malformed success envelope is ambiguous too: the POST might have succeeded.
          throw new Error("Confirmation returned no dataset record. Its outcome is uncertain; retry the identical request.");
        }
        this.queuedDataset = response.dataset;
        this.$emit("queued", response.dataset);
      } catch (error) {
        if (!this.current(epoch, controller)) return;
        this.error = importErrorMessage(error);
        this.retryAllowed = ambiguousImportError(error);
        this.needsRepreview = !this.retryAllowed;
      } finally {
        if (this.current(epoch, controller)) this.submitting = false;
      }
    },
    abandonAttempt() {
      if (this.submitting || !this.abandonAcknowledged) return;
      this.frozenPayload = null;
      this.retryAllowed = false;
      this.needsRepreview = false;
      this.abandonAcknowledged = false;
      this.error = "Previous attempt abandoned. Preview again before creating a new confirmation key. Check existing datasets for duplicates.";
      this.invalidatePreview();
    },
    startAnother() {
      this.frozenPayload = null;
      this.queuedDataset = null;
      this.sourceFileId = null;
      this.uploadFiles = [];
      this.invalidatePreview();
    },
  },
};
</script>

<style scoped>
.tabular-import { color: #23352c; padding: 18px; }
h3, h4 { color: #245c40; margin: 0 0 14px; }
h5 { margin: 16px 0 8px; }
fieldset, .review, .attempt { border: 1px solid #d5e4d9; border-radius: 10px; padding: 16px; margin: 16px 0; min-width: 0; }
legend { float: none; width: auto; font-size: 1rem; font-weight: 600; padding: 0 6px; }
.form-group { display: flex; flex-direction: column; gap: 6px; margin-bottom: 14px; font-weight: 500; }
input:not([type="checkbox"]), select, textarea { width: 100%; padding: 9px 11px; border: 1px solid #becfc3; border-radius: 6px; background: #fff; color: #23352c; font: inherit; box-sizing: border-box; }
textarea { font-family: monospace; resize: vertical; }
.checkbox-label { display: flex; align-items: flex-start; gap: 8px; margin: 12px 0; }
.checkbox-label input { margin-top: 4px; }
.option-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(210px, 1fr)); gap: 14px; }
.hint { color: #596c60; font-size: .9rem; }
.notice, .alert-error { border-radius: 6px; padding: 12px; white-space: pre-wrap; overflow-wrap: anywhere; }
.notice { background: #edf7f0; border: 1px solid #bfddc7; }
.alert-error { background: #fff0ef; border: 1px solid #e8b3af; color: #8c2520; }
.btn-primary, .btn-secondary { padding: 9px 15px; border-radius: 6px; cursor: pointer; margin: 4px 8px 4px 0; font: inherit; }
.btn-primary { background: #347b50; border: 1px solid #347b50; color: #fff; }
.btn-secondary { background: #fff; border: 1px solid #bccfc1; color: #315940; }
button:disabled, fieldset:disabled { opacity: .65; }
button:disabled { cursor: not-allowed; }
:is(input, select, textarea, button, summary):focus-visible { outline: 3px solid #65a67b; outline-offset: 2px; }
pre { max-height: 340px; overflow: auto; background: #f5f8f6; padding: 12px; font-size: .85rem; white-space: pre-wrap; overflow-wrap: anywhere; }
summary { cursor: pointer; padding: 8px 0; font-weight: 500; }
.table-scroll { overflow: auto; }
table { width: 100%; border-collapse: collapse; }
th, td { padding: 8px; border-bottom: 1px solid #dce7df; text-align: left; }
</style>